import { ReactElement, useMemo, useRef, useState } from 'react';
import styles from './task-subject-info.module.scss';
import listStyles from './task-info-item.module.scss';
import {
    UIButton,
    UIErrorState,
    UILoadingState,
    UIMenu,
    UIMenuItem,
    UIPopover,
    UIPropoverRef
} from '@/ui';
import { blue } from '@/ui/styles';
import { TaskInfoItem, TaskInfoItemProps } from './task-info-item';
import { useTaskInfo } from '../providers';
import {
    CompanyUser,
    Contact,
    Device,
    GetTaskInfoResponseData,
    UserDescription,
    UserMedical
} from '@/api';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useCompanyContext } from '@/companies/providers';
import { useFormatDate } from '@/common/hooks';
import { useTasksTranslation } from '../hooks';
import { formatHeight } from '../util/task-helpers';

type SelectedState =
    | 'user_description'
    | 'device'
    | 'medical_info'
    | 'next_of_kin'
    | 'none';

export function TaskSubjectInfo(): ReactElement {
    const { t } = useTasksTranslation();
    const { taskInfo, status, refetch } = useTaskInfo();

    return (
        <div className={styles.userInfoContainer}>
            {status === 'pending' && <UILoadingState />}
            {status === 'error' && (
                <UIErrorState
                    title={t('shared.errors.load_data')}
                    onRetry={refetch}
                />
            )}
            {status === 'success' && taskInfo && (
                <LoadedContent taskInfo={taskInfo} />
            )}
        </div>
    );
}

function LoadedContent(props: {
    taskInfo: GetTaskInfoResponseData;
}): ReactElement {
    const { taskInfo } = props;
    const { t } = useTasksTranslation();
    const ref = useRef<UIPropoverRef>(null);

    const {
        company: {
            features: { canViewLWDPhone }
        }
    } = useCompanyContext();
    const canRenderUserDescription = taskInfo.user && taskInfo.userDescription;
    const canRenderDevice = !!taskInfo.device;
    const canRenderMedicalInfo = taskInfo.user && taskInfo.userMedical;
    const canRenderNextOfKin = taskInfo.user && taskInfo.userContacts;
    const canRenderAny =
        canRenderUserDescription ||
        canRenderDevice ||
        canRenderMedicalInfo ||
        canRenderNextOfKin;

    const [selectedIndex, setSelectedIndex] = useState<SelectedState>(() => {
        if (canRenderUserDescription) {
            return 'user_description';
        } else if (canRenderDevice) {
            return 'device';
        } else if (canRenderMedicalInfo) {
            return 'medical_info';
        } else if (canRenderNextOfKin) {
            return 'next_of_kin';
        }
        return 'none';
    });

    const buttonText = useMemo(() => {
        switch (selectedIndex) {
            case 'user_description':
                return t('components.task_subject_info.tab_user_description');
            case 'device':
                return t('components.task_subject_info.tab_device');
            case 'medical_info':
                return t('components.task_subject_info.tab_medical');
            case 'next_of_kin':
                return t('components.task_subject_info.tab_next_of_kin');
            default:
                return t('components.task_subject_info.tab_none');
        }
    }, [selectedIndex, t]);

    function onClick(state: SelectedState) {
        setSelectedIndex(state);
        ref.current?.close();
    }

    return (
        <>
            {canRenderAny ? (
                <>
                    <div className={styles.sectionHeaderWrapper}>
                        <UIPopover
                            ref={ref}
                            placement="bottom_right"
                            popoverPadding={4}
                            trigger="click"
                            triggerNode={
                                <button className={styles.removeButtonStyle}>
                                    <UIButton
                                        variant="textPrimary"
                                        rightIcon="chevron-down"
                                        withoutButtonHtmlTag
                                        customPadding={8}
                                    >
                                        {buttonText}
                                    </UIButton>
                                </button>
                            }
                        >
                            <UIMenu>
                                {canRenderUserDescription && (
                                    <UIMenuItem
                                        text={t(
                                            'components.task_subject_info.tab_user_description'
                                        )}
                                        onClick={() => {
                                            onClick('user_description');
                                        }}
                                        leftIconName={
                                            selectedIndex === 'user_description'
                                                ? 'check'
                                                : undefined
                                        }
                                        leftIconColor={blue}
                                    />
                                )}
                                {canRenderDevice && (
                                    <UIMenuItem
                                        text={t(
                                            'components.task_subject_info.tab_device'
                                        )}
                                        onClick={() => {
                                            onClick('device');
                                        }}
                                        leftIconName={
                                            selectedIndex === 'device'
                                                ? 'check'
                                                : undefined
                                        }
                                        leftIconColor={blue}
                                    />
                                )}
                                {canRenderMedicalInfo && (
                                    <UIMenuItem
                                        text={t(
                                            'components.task_subject_info.tab_medical'
                                        )}
                                        onClick={() => {
                                            onClick('medical_info');
                                        }}
                                        leftIconName={
                                            selectedIndex === 'medical_info'
                                                ? 'check'
                                                : undefined
                                        }
                                        leftIconColor={blue}
                                    />
                                )}
                                {canRenderNextOfKin && (
                                    <UIMenuItem
                                        text={t(
                                            'components.task_subject_info.tab_next_of_kin'
                                        )}
                                        onClick={() => {
                                            onClick('next_of_kin');
                                        }}
                                        leftIconName={
                                            selectedIndex === 'next_of_kin'
                                                ? 'check'
                                                : undefined
                                        }
                                        leftIconColor={blue}
                                    />
                                )}
                            </UIMenu>
                        </UIPopover>
                    </div>
                    {selectedIndex === 'user_description' &&
                        canRenderUserDescription && (
                            <TaskUserDescriptionList
                                companyUser={taskInfo.companyUser}
                                userDescription={taskInfo.userDescription!}
                            />
                        )}

                    {selectedIndex === 'device' && canRenderDevice && (
                        <TaskDeviceList
                            device={taskInfo.device!}
                            canViewLWDPhone={canViewLWDPhone}
                        />
                    )}
                    {selectedIndex === 'medical_info' &&
                        canRenderMedicalInfo && (
                            <TaskUserMedicalList
                                userMedical={taskInfo.userMedical!}
                            />
                        )}
                    {selectedIndex === 'next_of_kin' && canRenderNextOfKin && (
                        <TaskUserNextOfKinList
                            userContacts={taskInfo.userContacts!}
                        />
                    )}
                    {selectedIndex === 'none' && (
                        <UIErrorState
                            title={t('components.task_subject_info.empty')}
                        />
                    )}
                </>
            ) : (
                <UIErrorState title={t('components.task_subject_info.empty')} />
            )}
        </>
    );
}

function TaskUserDescriptionList(props: {
    // Name and phone live on the company user, which the task info endpoint
    // resolves for us. Null when the task has no user, or none could be found.
    companyUser: CompanyUser | null;
    userDescription: UserDescription;
}): ReactElement {
    const { companyUser, userDescription } = props;
    const { t } = useTasksTranslation();
    // Was `toLocaleDateString()` with no locale and no timezone, so a US
    // browser saw US order whatever `?lng=` said. This is the same formatter
    // the rest of the module uses.
    const { formatDateOnly } = useFormatDate();

    const items: TaskInfoItemProps[] = useMemo(() => {
        const notSet = t('shared.labels.not_set');

        const phone = companyUser?.phone
            ? parsePhoneNumberFromString(
                  companyUser.phone
              )?.formatInternational()
            : null;
        const secondaryPhone = companyUser?.phone2
            ? parsePhoneNumberFromString(
                  companyUser.phone2
              )?.formatInternational()
            : null;

        return [
            {
                title: t('shared.labels.name'),
                text: companyUser
                    ? `${companyUser.firstName ?? ''} ${
                          companyUser.lastName ?? ''
                      }`.trim() || notSet
                    : notSet
            },
            { title: t('shared.labels.phone'), phone: phone ?? notSet },
            {
                title: t(
                    'components.task_user_description_list.secondary_phone'
                ),
                phone: secondaryPhone ?? notSet
            },
            {
                title: t('components.task_user_description_list.sex'),
                text: userDescription?.sex ?? notSet
            },
            {
                title: t('components.task_user_description_list.date_of_birth'),
                text: userDescription?.dateOfBirth
                    ? formatDateOnly(new Date(userDescription.dateOfBirth))
                    : notSet
            },
            {
                title: t('components.task_user_description_list.race'),
                text: userDescription?.race ?? notSet
            },
            {
                title: t('components.task_user_description_list.height'),
                text: userDescription?.height
                    ? formatHeight(
                          t,
                          userDescription.height.value,
                          userDescription.height.unit
                      )
                    : notSet
            },
            {
                title: t('components.task_user_description_list.hair_colour'),
                text: userDescription?.hairColour ?? notSet
            },
            {
                title: t('components.task_user_description_list.eye_colour'),
                text: userDescription?.eyeColour ?? notSet
            },
            {
                title: t(
                    'components.task_user_description_list.identifying_features'
                ),
                text:
                    userDescription?.identifyingFeatures &&
                    userDescription.identifyingFeatures.length > 0
                        ? userDescription?.identifyingFeatures.join(
                              t('shared.tasks.list_separator')
                          )
                        : notSet
            },
            {
                title: t(
                    'components.task_user_description_list.vehicle_number_plate'
                ),
                text: userDescription?.vehicleNumberPlate ?? notSet
            }
        ];
    }, [companyUser, userDescription, t, formatDateOnly]);

    return (
        <div className={listStyles.container}>
            <div className={listStyles.listContainer}>
                {items.map((props, index) => (
                    <TaskInfoItem key={index} {...props} />
                ))}
            </div>
        </div>
    );
}

function TaskDeviceList(props: {
    device: Device;
    canViewLWDPhone: boolean;
}): ReactElement {
    const { device, canViewLWDPhone } = props;
    const { t } = useTasksTranslation();

    const items: TaskInfoItemProps[] = useMemo(() => {
        const notSet = t('shared.labels.not_set');

        const items: TaskInfoItemProps[] = [
            {
                title: t('shared.labels.name'),
                text: device ? device.name : notSet
            },
            {
                title: t('components.task_device_list.device_id'),
                text: device ? device.id : notSet
            }
        ];

        if (canViewLWDPhone) {
            const phone = device.phone
                ? parsePhoneNumberFromString(
                      device.phone
                  )?.formatInternational()
                : null;

            items.push({
                title: t('shared.labels.phone'),
                phone: phone ?? notSet
            });
        }

        return items;
    }, [device, canViewLWDPhone, t]);

    return (
        <div className={listStyles.container}>
            <div className={listStyles.listContainer}>
                {items.map((props, index) => (
                    <TaskInfoItem key={index} {...props} />
                ))}
            </div>
        </div>
    );
}

function TaskUserMedicalList(props: {
    userMedical: UserMedical;
}): ReactElement {
    const { userMedical } = props;
    const { t } = useTasksTranslation();

    const items: TaskInfoItemProps[] = useMemo(() => {
        const notSet = t('shared.labels.not_set');
        const separator = t('shared.tasks.list_separator');

        return [
            {
                title: t('components.task_user_medical_list.blood_type'),
                text: userMedical.bloodType ?? notSet
            },
            {
                title: t('components.task_user_medical_list.allergies'),
                text:
                    userMedical.allergies.length > 0
                        ? userMedical.allergies.join(separator)
                        : notSet
            },
            {
                title: t('components.task_user_medical_list.conditions'),
                text:
                    userMedical.conditions.length > 0
                        ? userMedical.conditions.join(separator)
                        : notSet
            },
            {
                title: t('components.task_user_medical_list.medication'),
                text:
                    userMedical.medication.length > 0
                        ? userMedical.medication.join(separator)
                        : notSet
            }
        ];
    }, [userMedical, t]);

    return (
        <div className={listStyles.container}>
            <div className={listStyles.listContainer}>
                {items.map((props, index) => (
                    <TaskInfoItem key={index} {...props} />
                ))}
            </div>
        </div>
    );
}

function TaskUserNextOfKinList(props: {
    userContacts: Contact[];
}): ReactElement {
    const { userContacts } = props;
    const { t } = useTasksTranslation();

    const items: TaskInfoItemProps[] = useMemo(() => {
        return userContacts.map((contact) => {
            const phone = parsePhoneNumberFromString(contact.phone);
            return {
                title: t('shared.tasks.contact_name_with_role', {
                    name: contact.name,
                    role: contact.role
                }),
                phone: phone?.formatInternational()
            };
        });
    }, [userContacts, t]);

    return (
        <div className={listStyles.container}>
            <div className={listStyles.listContainer}>
                {items.map((props, index) => (
                    <TaskInfoItem key={index} {...props} />
                ))}
                {items.length === 0 && (
                    <UIErrorState
                        title={t('components.task_user_next_of_kin_list.empty')}
                    />
                )}
            </div>
        </div>
    );
}
